import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, addDoc,
  collection, query, where, orderBy, getDocs,
  writeBatch, serverTimestamp
} from "firebase/firestore";
import {
  ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject
} from "firebase/storage";

import { auth, firestore, storage } from "./FirebaseService";
import { DoctorRoles, PatientRoles } from "../config/DiactiveTypes";
import { getPatientHasAppAccess } from "./PatientService";

const usersCollectionName = "users";
const tutorialsCollectionName = "tutorials";
const tutorialsStoragePrefix = "tutorialsVideos";

export const TutorialAudience = {
  Doctor: "doctor",
  Patient: "patient"
};

export const TutorialDependency = {
  HasAppAccess: "hasAppAccess"
};

export const MaxVideoSizeBytes = 100 * 1024 * 1024;
export const MaxVideoSizeMB = MaxVideoSizeBytes / (1024 * 1024);

// Memoize download URLs per storagePath for the session so repeat views don't
// re-issue getDownloadURL calls. Cleared on upload/delete of the same path.
const downloadUrlCache = new Map();

const storagePathFor = (tutorialId) => `${tutorialsStoragePrefix}/${tutorialId}.mp4`;

const assertValidVideoFile = (file) => {
  if (!file) {
    throw new Error("Se requiere un archivo de vídeo");
  }
  if (!file.type || !file.type.startsWith("video/")) {
    throw new Error("El archivo debe ser un vídeo");
  }
  if (file.size > MaxVideoSizeBytes) {
    throw new Error(`El vídeo supera el tamaño máximo permitido (${MaxVideoSizeMB} MB)`);
  }
};

// Upload with long-lived cache headers so repeat views are served from the
// browser cache. Firebase generates a fresh download token on each upload, so
// the URL returned by getDownloadURL naturally busts the cache when the video
// is replaced.
const uploadVideoFile = (storagePath, file, onProgress) => {
  const fileRef = ref(storage, storagePath);
  const metadata = {
    contentType: file.type || "video/mp4",
    cacheControl: "public, max-age=31536000, immutable"
  };

  if (!onProgress) {
    return uploadBytes(fileRef, file, metadata);
  }

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file, metadata);
    task.on(
      "state_changed",
      (snap) => {
        const fraction = snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0;
        onProgress(fraction);
      },
      reject,
      () => resolve(task.snapshot)
    );
  });
};

export const fetchTutorials = async ({ audience }) => {
  try {
    const q = query(
      collection(firestore, tutorialsCollectionName),
      where("audience", "==", audience),
      orderBy("order", "asc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  catch (error) {
    console.error(error);
    return [];
  }
};

export const getTutorial = async (id) => {
  const snap = await getDoc(doc(firestore, tutorialsCollectionName, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getTutorialVideoUrl = async (storagePath) => {
  if (!storagePath) return null;
  if (downloadUrlCache.has(storagePath)) {
    return downloadUrlCache.get(storagePath);
  }
  const url = await getDownloadURL(ref(storage, storagePath));
  downloadUrlCache.set(storagePath, url);
  return url;
};

export const createTutorial = async ({ title, audience, order, dependency, file, onProgress }) => {
  assertValidVideoFile(file);

  const placeholder = {
    title,
    audience,
    order,
    ...(dependency ? { dependency } : {}),
    storagePath: "",
    sizeBytes: file.size,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    uploaderId: auth.currentUser ? auth.currentUser.uid : null
  };

  const docRef = await addDoc(collection(firestore, tutorialsCollectionName), placeholder);
  const storagePath = storagePathFor(docRef.id);

  try {
    await uploadVideoFile(storagePath, file, onProgress);
    const uid = auth.currentUser?.uid ?? null;
    await updateDoc(docRef, {
      storagePath,
      updatedAt: serverTimestamp(),
      updatedById: uid,
    });
  }
  catch (err) {
    // Roll back the placeholder doc if the upload fails so we don't leave
    // orphaned metadata pointing at a non-existent file.
    await deleteDoc(docRef).catch(() => {});
    throw err;
  }

  return { id: docRef.id, ...placeholder, storagePath };
};

export const updateTutorial = async (id, { title, order, dependency, file, onProgress } = {}) => {
  const docRef = doc(firestore, tutorialsCollectionName, id);
  const update = {
    updatedAt: serverTimestamp(),
    updatedById: auth.currentUser?.uid ?? null,
  };

  if (title !== undefined) update.title = title;
  if (order !== undefined) update.order = order;

  // Allow clearing the dependency by passing null; leave untouched when undefined.
  if (dependency !== undefined) {
    update.dependency = dependency || null;
  }

  if (file) {
    assertValidVideoFile(file);
    const storagePath = storagePathFor(id);
    await uploadVideoFile(storagePath, file, onProgress);
    update.storagePath = storagePath;
    update.sizeBytes = file.size;
    downloadUrlCache.delete(storagePath);
  }

  await updateDoc(docRef, update);
};

export const deleteTutorial = async (id) => {
  const docRef = doc(firestore, tutorialsCollectionName, id);
  const snap = await getDoc(docRef);

  if (snap.exists()) {
    const storagePath = snap.data().storagePath;
    if (storagePath) {
      try {
        await deleteObject(ref(storage, storagePath));
      }
      catch (err) {
        // Missing file is fine — still proceed to delete the doc.
        if (err.code !== "storage/object-not-found") throw err;
      }
      downloadUrlCache.delete(storagePath);
    }
  }

  await deleteDoc(docRef);
};

export const reorderTutorials = async (orderedIds) => {
  const batch = writeBatch(firestore);
  orderedIds.forEach((id, index) => {
    batch.update(doc(firestore, tutorialsCollectionName, id), {
      order: index,
      updatedAt: serverTimestamp(),
      updatedById: auth.currentUser?.uid ?? null,
    });
  });
  await batch.commit();
};

export const filterTutorialsByAppAccess = (tutorials, hasAppAccess) =>
  tutorials.filter(
    (t) => t.dependency !== TutorialDependency.HasAppAccess || hasAppAccess
  );

/** Tutorials with a uploaded video ready to play. */
export const getPlayableTutorials = (tutorials) =>
  tutorials.filter((t) => t.storagePath);

export const buildProgressForTutorialList = (tutorialCount, savedProgress) => {
  const progress = Array(tutorialCount).fill(false);
  if (!savedProgress?.length) return progress;
  const minLength = Math.min(savedProgress.length, tutorialCount);
  for (let i = 0; i < minLength; i++) {
    progress[i] = savedProgress[i] === true;
  }
  return progress;
};

export const isProgressCompleteForTutorialCount = (progress, tutorialCount) => {
  if (tutorialCount === 0) return true;
  for (let i = 0; i < tutorialCount; i++) {
    if (progress[i] !== true) return false;
  }
  return true;
};

export const hasCompletedTutorials = async (userId, role) => {
  const audience = DoctorRoles.includes(role)
    ? TutorialAudience.Doctor
    : TutorialAudience.Patient;

  let hasAppAccess = true;
  if (PatientRoles.includes(role)) {
    hasAppAccess = await getPatientHasAppAccess(userId);
  }

  const all = await fetchTutorials({ audience });
  const filtered = getPlayableTutorials(filterTutorialsByAppAccess(all, hasAppAccess));
  const tutorialCount = filtered.length;
  if (tutorialCount === 0) return true;

  const savedProgress = await loadProgress(userId);
  const progress = buildProgressForTutorialList(tutorialCount, savedProgress);
  return isProgressCompleteForTutorialCount(progress, tutorialCount);
};

export const loadProgress = async (userId) => {
  const docRef = doc(firestore, usersCollectionName, userId, tutorialsCollectionName, "progress");
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data().progress;
  }
  else {
    return null;
  }
};

export const saveProgress = async (userId, progress) => {
  const docRef = doc(firestore, usersCollectionName, userId, tutorialsCollectionName, "progress");
  const docSnap = await getDoc(docRef);

  const data = { progress };

  if (!docSnap.exists()) {
    data.createdAt = serverTimestamp();
  }
  else {
    const existingData = docSnap.data();
    const allCompleted = isProgressCompleteForTutorialCount(progress, progress.length);
    if (allCompleted && !existingData.completedAt) {
      data.completedAt = serverTimestamp();
    }
  }

  await setDoc(docRef, data, { merge: true });
};
